/**
 * Rescue Ready Template Component
 * Pre-defined emergency rescue protocol with compression tracking
 */
import { Phone, Heart } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { RESCUE_READY_ACTIONS, DRILL_ACTIONS, DRILL_STATES } from '@/types/drillTimer'
import { cn } from '@/utils/helpers'

export function RescueReadyTemplate({ onLog, records = [] }) {
  // State for each action
  const [actionStates, setActionStates] = useState(() => {
    const initial = {}
    RESCUE_READY_ACTIONS.forEach((action) => {
      initial[action.definition] = {
        currentStateIndex: 0,
        completed: false,
        skipped: false,
      }
    })
    return initial
  })

  const [is911Called, setIs911Called] = useState(false)
  const [compressionsActive, setCompressionsActive] = useState(false)

  // Restore state from records when component mounts or records change
  useEffect(() => {
    if (records.length === 0) return

    const newActionStates = {}
    RESCUE_READY_ACTIONS.forEach((action) => {
      newActionStates[action.definition] = {
        currentStateIndex: 0,
        completed: false,
        skipped: false,
      }
    })

    // Process records to restore action states
    RESCUE_READY_ACTIONS.forEach((action) => {
      // COMPRESSIONS_RERUN logs as COMPRESSIONS with RESTARTED state
      const actionToMatch =
        action.definition === DRILL_ACTIONS.COMPRESSIONS_RERUN
          ? DRILL_ACTIONS.COMPRESSIONS
          : action.definition

      const stateToMatch =
        action.definition === DRILL_ACTIONS.COMPRESSIONS_RERUN ? DRILL_STATES.RESTARTED : null

      const actionRecords = records.filter((r) => {
        if (stateToMatch) {
          return r.action === actionToMatch && r.state === stateToMatch
        }
        // For non-compressions_rerun, exclude RESTARTED/STOPPED states from matching COMPRESSIONS
        if (action.definition === DRILL_ACTIONS.COMPRESSIONS) {
          return (
            r.action === actionToMatch &&
            r.state !== DRILL_STATES.RESTARTED &&
            r.state !== DRILL_STATES.STOPPED
          )
        }
        return r.action === actionToMatch
      })

      if (actionRecords.length > 0) {
        const latestRecord = actionRecords[actionRecords.length - 1]

        if (latestRecord.state === DRILL_STATES.SKIPPED) {
          newActionStates[action.definition].skipped = true
        } else {
          // Find the state index in the action's states array
          const stateIndex = action.states.indexOf(latestRecord.state)
          if (stateIndex !== -1) {
            newActionStates[action.definition].currentStateIndex = stateIndex
            // If it's the last state, mark as completed
            if (stateIndex === action.states.length - 1) {
              newActionStates[action.definition].completed = true
            } else {
              // Move to next state since this one was logged
              newActionStates[action.definition].currentStateIndex = stateIndex + 1
            }
          }
        }
      }
    })

    setActionStates(newActionStates)

    // Restore 911 called state
    const called911Record = records.find(
      (r) => r.action === DRILL_ACTIONS.CALLED_911 && r.state === DRILL_STATES.COMPLETED,
    )
    setIs911Called(!!called911Record)

    // Restore compressions active state
    const compressionRecords = records.filter((r) => r.action === DRILL_ACTIONS.COMPRESSIONS)
    if (compressionRecords.length > 0) {
      const lastCompressionRecord = compressionRecords[compressionRecords.length - 1]
      setCompressionsActive(
        lastCompressionRecord.state === DRILL_STATES.STARTED ||
          lastCompressionRecord.state === DRILL_STATES.RESTARTED,
      )
    }
  }, [records])

  // Show heart button after compressions_rerun is started
  const showHeartButton =
    actionStates[DRILL_ACTIONS.COMPRESSIONS_RERUN]?.completed ||
    actionStates[DRILL_ACTIONS.COMPRESSIONS_RERUN]?.currentStateIndex > 0

  const handleActionClick = (actionDef) => {
    const action = RESCUE_READY_ACTIONS.find((a) => a.definition === actionDef)
    if (!action) return

    const state = actionStates[actionDef]
    if (state.completed) return

    // Auto-skip logic
    const currentIndex = RESCUE_READY_ACTIONS.findIndex((a) => a.definition === actionDef)
    const updatedStates = { ...actionStates }

    RESCUE_READY_ACTIONS.forEach((a, idx) => {
      if (
        idx < currentIndex &&
        !updatedStates[a.definition].completed &&
        !updatedStates[a.definition].skipped
      ) {
        updatedStates[a.definition].skipped = true
        onLog(a.definition, DRILL_STATES.SKIPPED)
      }
    })

    // Handle compression state changes
    if (actionDef === DRILL_ACTIONS.COMPRESSIONS) {
      startCompressions()
    } else if (actionDef === DRILL_ACTIONS.COMPRESSIONS_RERUN) {
      resumeCompressions()
    } else if (
      compressionsActive &&
      actionDef !== DRILL_ACTIONS.AED &&
      actionDef !== DRILL_ACTIONS.COMPRESSIONS
    ) {
      pauseCompressions()
    }

    // Log the action
    const currentState = action.states[state.currentStateIndex]
    const logAction =
      actionDef === DRILL_ACTIONS.COMPRESSIONS_RERUN ? DRILL_ACTIONS.COMPRESSIONS : actionDef
    onLog(logAction, currentState)

    // Update action state
    if (state.currentStateIndex === action.states.length - 1) {
      updatedStates[actionDef].completed = true
    } else {
      updatedStates[actionDef].currentStateIndex += 1
    }

    setActionStates(updatedStates)

    // Log 911 call as skipped if not made when drill action is clicked
    if (actionDef === DRILL_ACTIONS.DRILL && !is911Called) {
      onLog(DRILL_ACTIONS.CALLED_911, DRILL_STATES.SKIPPED)
    }
  }

  const startCompressions = () => {
    setCompressionsActive(true)
  }

  const pauseCompressions = () => {
    setCompressionsActive(false)
    onLog(DRILL_ACTIONS.COMPRESSIONS, DRILL_STATES.STOPPED)
  }

  const resumeCompressions = () => {
    setCompressionsActive(true)
  }

  const toggleCompressions = () => {
    if (compressionsActive) {
      pauseCompressions()
    } else {
      resumeCompressions()
      onLog(DRILL_ACTIONS.COMPRESSIONS, DRILL_STATES.RESTARTED)
    }
  }

  const handle911Call = () => {
    if (!is911Called) {
      onLog(DRILL_ACTIONS.CALLED_911, DRILL_STATES.COMPLETED)
      setIs911Called(true)
    }
  }

  const getCardStyle = (actionDef) => {
    const state = actionStates[actionDef]
    if (state.skipped) return 'bg-red-50 border-red-500 border-2'
    if (state.completed) return 'bg-green-50 border-green-500 border-2'
    if (state.currentStateIndex > 0) return 'bg-blue-50 border-blue-500 border-2 cursor-pointer'
    return 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
  }

  return (
    <div className="flex h-full flex-col">
      {/* Action Cards */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4 pb-32">
        {RESCUE_READY_ACTIONS.map((action) => {
          const state = actionStates[action.definition]
          const currentStateName = action.states[state.currentStateIndex]

          return (
            <Card
              key={action.definition}
              className={cn('transition-all duration-200', getCardStyle(action.definition), {
                'hover:scale-[1.02]': !state.completed,
              })}
              onClick={() => !state.completed && handleActionClick(action.definition)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-medium">{action.name}</div>
                    {action.hint && (
                      <div className="text-muted-foreground mt-1 text-sm">{action.hint}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {!state.completed && !state.skipped && (
                      <Badge variant="outline">{currentStateName}</Badge>
                    )}
                    {state.completed && <Badge className="bg-green-600">Completed</Badge>}
                    {state.skipped && <Badge variant="destructive">Skipped</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Bottom Action Bar */}
      {/* Use z-index > 50 to hide the Feedback button on this view */}
      <div className="bg-primary text-primary-foreground fixed right-0 bottom-0 left-0 z-60 border-t p-4">
        <div className="mx-auto flex max-w-md items-center justify-center gap-3">
          {/* 911 Call Button */}
          <Button
            variant={is911Called ? 'secondary' : 'default'}
            size="lg"
            onClick={handle911Call}
            disabled={is911Called}
            className={cn(
              'flex h-20 flex-1 flex-col items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105',
              {
                'bg-green-600 text-white hover:bg-green-700': is911Called,
                'text-primary bg-white hover:bg-gray-100': !is911Called,
              },
            )}
          >
            <Phone className={cn('h-8 w-8', { 'text-white': is911Called })} />
            <span className="text-sm font-semibold">
              {is911Called ? '911 Called' : 'Mark 911 as Called'}
            </span>
          </Button>

          {/* Compression Toggle Button */}
          {showHeartButton && (
            <Button
              variant="default"
              size="lg"
              onClick={toggleCompressions}
              className={cn(
                'flex h-20 flex-1 flex-col items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105',
                {
                  'animate-pulse bg-red-600 text-white hover:bg-red-700': compressionsActive,
                  'text-primary bg-white hover:bg-gray-100': !compressionsActive,
                },
              )}
            >
              <Heart
                className={cn('h-8 w-8', {
                  'fill-red-600 text-red-600': compressionsActive,
                })}
              />
              <span className="text-sm font-semibold">
                {compressionsActive ? 'Compressions Active' : 'Compressions Inactive'}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

RescueReadyTemplate.propTypes = {
  onLog: PropTypes.func.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      state: PropTypes.string,
      time: PropTypes.string,
    }),
  ),
}
